import React, { FC, useCallback, useState } from 'react';

import { Box, Spinner, Text } from '@chakra-ui/react';

import { EVerificationMethod } from './types';
import { VerificationMethodItem } from './VerificationMethodItem';
import { EColor, ETextVariant } from '../../../Theme';
import { useRequestCode } from '../../api';
import { EVerificationStep } from '../../context';
import { useVerificationStepper } from '../../hooks.ts';
import { DEFAULT_ERROR_MESSAGE } from '../constants';
import { EVerificationStatus } from '../types';
import { getRequestCodeArgs, maskEmail, maskPhone } from '../utils';

interface IVerificationMethodStep {
  email: string | undefined;
  phone: string | undefined;
  monolithBaseUrl: string;
  changeMethod: (method: EVerificationMethod) => void;
}

export const VerificationMethodStep: FC<IVerificationMethodStep> = ({
  email,
  phone,
  monolithBaseUrl,
  changeMethod,
}) => {
  const { setStep } = useVerificationStepper();
  const [error, setError] = useState<string | null>(null);

  const { mutateAsync: requestCode, isPending: requestCodeIsLoading } =
    useRequestCode(monolithBaseUrl);

  const requestCodeHandler = useCallback(
    async (method: EVerificationMethod) => {
      if (error) {
        setError(null);
      }

      try {
        const args = getRequestCodeArgs(method, phone, email);

        if (args) {
          const data = await requestCode(args);
          if (data?.status === EVerificationStatus.PENDING) {
            changeMethod(method);
            setStep(EVerificationStep.VERIFICATION_CODE);
          }
        }
      } catch (err) {
        setError(DEFAULT_ERROR_MESSAGE);
      }
    },
    [changeMethod, error, email, phone, requestCode, setStep]
  );

  return (
    <Box>
      <Text as="h1" mb="24px" variant={ETextVariant.XL2}>
        Two-factor authentication required.
      </Text>
      {requestCodeIsLoading ? (
        <Box h="100vh" mt="50px" textAlign="center">
          <Spinner />
        </Box>
      ) : (
        <Box>
          <Box>
            {phone && (
              <VerificationMethodItem
                clickHandler={() =>
                  requestCodeHandler(EVerificationMethod.PHONE)
                }
                content={phone}
                format={maskPhone}
                method={EVerificationMethod.PHONE}
              />
            )}
            {email && (
              <VerificationMethodItem
                clickHandler={() =>
                  requestCodeHandler(EVerificationMethod.EMAIL)
                }
                content={email}
                format={maskEmail}
                method={EVerificationMethod.EMAIL}
              />
            )}
          </Box>

          {error && (
            <Text
              color={EColor.Tomato}
              fontSize="10px"
              fontWeight={500}
              marginBottom={0}
              marginTop="8px"
            >
              {error}
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
};
